import { Heart, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useGetUsersFavoriteCleanersListQuery } from '@/hooks/queries/use-get-users-favorite-cleaners-list.query';
import { usePostUsersFavoriteCleanersMutation } from '@/hooks/mutations/use-post-users-favorite-cleaners.mutation';
import { useDeleteUsersFavoriteCleanersMutation } from '@/hooks/mutations/use-delete-users-favorite-cleaners.mutation';

interface FavoriteCleanerButtonProps {
  cleanerId: string;
}

export function FavoriteCleanerButton({
  cleanerId,
}: FavoriteCleanerButtonProps) {
  const addFavoriteMutation = usePostUsersFavoriteCleanersMutation();
  const removeFavoriteMutation = useDeleteUsersFavoriteCleanersMutation();

  const { data: favoriteCleanersData } = useGetUsersFavoriteCleanersListQuery();
  const favoriteCleaners = favoriteCleanersData?.data || [];

  const isFavorite = favoriteCleaners.some(
    favorite => favorite.cleaner_id === cleanerId
  );
  const favoriteData = favoriteCleaners.find(
    favorite => favorite.cleaner_id === cleanerId
  );

  const handleToggleFavorite = async () => {
    if (isFavorite && favoriteData) {
      // Remove from favorites — mutation self-invalidates favorite-cleaners cache.
      try {
        await removeFavoriteMutation.mutateAsync(favoriteData.favorite_id);
      } catch {
        // Handle error silently or show toast notification
      }
    } else {
      // Add to favorites — mutation self-invalidates favorite-cleaners cache.
      try {
        await addFavoriteMutation.mutateAsync({ cleaner_id: cleanerId });
      } catch {
        // Handle error silently or show toast notification
      }
    }
  };

  const isLoading =
    addFavoriteMutation.isPending || removeFavoriteMutation.isPending;

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleToggleFavorite}
      disabled={isLoading}
      className="h-8 w-8 p-0 hover:bg-red-50 dark:hover:bg-red-900/20"
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
      ) : (
        <Heart
          className={cn(
            'h-4 w-4',
            isFavorite
              ? 'fill-red-500 text-red-500'
              : 'text-gray-400 hover:text-red-500'
          )}
        />
      )}
    </Button>
  );
}
